import React, { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import DOMPurify from "dompurify";
import { RichTextEditor } from "@/components/RichTextEditor";
import { toast } from "sonner";
import { usePeriodeStore } from "@/hooks/use-periode";
import Cookies from "js-cookie";
import { UploadCloud, X, Loader2, FileText } from "lucide-react";

export const AjukanPertanyaanForm = ({
  setOpenPesan,
}: {
  setOpenPesan: (open: boolean) => void;
}) => {
  const [judulPesan, setJudulPesan] = useState("");
  const [isiPesan, setIsiPesan] = useState("");
  const [images, setImages] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const { getYearAndSemester } = usePeriodeStore();
  const { year, semester } = getYearAndSemester();

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFiles = Array.from(e.dataTransfer.files).filter(
        (file) =>
          file.type.startsWith("image/") || file.type === "application/pdf",
      );
      if (droppedFiles.length > 0) {
        setImages((prev) => {
          const newImages = [...prev, ...droppedFiles];
          if (newImages.length > 5) {
            toast.error("Maksimal 5 file yang dapat diunggah");
            return newImages.slice(0, 5);
          }
          return newImages;
        });
      } else {
        toast.error("Hanya file gambar dan PDF yang diperbolehkan");
      }
    }
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFiles = Array.from(e.target.files).filter(
        (file) =>
          file.type.startsWith("image/") || file.type === "application/pdf",
      );
      setImages((prev) => {
        const newImages = [...prev, ...selectedFiles];
        if (newImages.length > 5) {
          toast.error("Maksimal 5 file yang dapat diunggah");
          return newImages.slice(0, 5);
        }
        return newImages;
      });
    }
  };

  const removeImage = (indexToRemove: number) => {
    setImages((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const handleSubmit = async () => {
    if (!judulPesan.trim()) {
      toast.error("Judul tidak boleh kosong");
      return;
    }
    const isIsiPesanEmpty = !isiPesan.replace(/<[^>]*>/g, "").trim();
    if (!isiPesan.trim() || isIsiPesanEmpty) {
      toast.error("Isi pesan tidak boleh kosong");
      return;
    }

    setIsSubmitting(true);
    try {
      const activeToken = Cookies.get("auth_token");

      const formData = new FormData();
      formData.append("judul", judulPesan);
      formData.append("isi", DOMPurify.sanitize(isiPesan));

      images.forEach((img) => {
        formData.append("images", img);
      });

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/api/v1/pengaduan?year=${year}&semester=${semester}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${activeToken}`,
          },
          body: formData,
        },
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || "Gagal mengirim pengajuan");
      }

      toast.success("Pesan berhasil dikirim");
      setOpenPesan(false);
      setJudulPesan("");
      setIsiPesan("");
      setImages([]);
    } catch (error: any) {
      console.error(error);
      toast.error(error.message || "Terjadi kesalahan pada server");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DialogContent className="sm:max-w-2xl max-h-[90vh] p-0 flex flex-col overflow-hidden gap-0">
      <DialogHeader className="px-6 py-4 border-b border-slate-100 bg-white shrink-0">
        <DialogTitle>Ajukan Pertanyaan atau Masukan</DialogTitle>
        <DialogDescription>
          Silakan sampaikan pertanyaan, atau masukan Anda terkait dengan data
          remunerasi ini.
        </DialogDescription>
      </DialogHeader>

      <div className="px-6 py-4 flex-1 overflow-y-auto custom-scrollbar">
        <div className="grid gap-5">
          <div className="flex flex-col gap-2">
            <Label htmlFor="judul" className="font-semibold text-slate-700">
              Judul
            </Label>
            <Input
              id="judul"
              placeholder="Contoh: Pertanyaan terkait poin iku"
              className="focus-visible:ring-primary"
              value={judulPesan}
              onChange={(e) => setJudulPesan(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="isi" className="font-semibold text-slate-700">
              Isi Pesan
            </Label>
            <RichTextEditor
              value={isiPesan}
              onChange={setIsiPesan}
              placeholder="Tulis detail pertanyaan atau masukan disini..."
              minHeight="min-h-36"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label className="font-semibold text-slate-700">
              Lampiran Gambar (Opsional)
            </Label>

            {images.length > 0 && (
              <div className="flex flex-wrap gap-3 mb-1">
                {images.map((img, idx) => (
                  <div
                    key={idx}
                    className="relative group rounded-md overflow-hidden border border-slate-200 bg-slate-100 w-20 h-20 flex flex-col items-center justify-center"
                    title={img.name}
                  >
                    {img.type === "application/pdf" ? (
                      <div className="flex flex-col items-center justify-center h-full w-full bg-slate-50 text-slate-400 p-2 text-center">
                        <FileText size={28} className="mb-1 text-slate-500" />
                        <span className="text-[9px] leading-tight font-medium truncate w-full px-1">
                          {img.name}
                        </span>
                      </div>
                    ) : (
                      <img
                        src={URL.createObjectURL(img)}
                        alt="preview"
                        className="w-full h-full object-cover"
                      />
                    )}
                    <button
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeImage(idx);
                      }}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {images.length < 5 && (
              <div
                className={`border-2 border-dashed rounded-lg p-6 flex flex-col items-center justify-center text-center transition-colors cursor-pointer
                ${isDragging ? "border-primary bg-primary/5" : "border-slate-300 hover:border-slate-400 bg-slate-50"}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => document.getElementById("file-upload")?.click()}
              >
                <UploadCloud
                  className={`w-8 h-8 mb-2 ${isDragging ? "text-primary" : "text-slate-400"}`}
                />
                <p className="text-sm text-slate-600">
                  Seret & lepas file ke sini atau klik untuk{" "}
                  <span className="text-blue-500 font-medium">pilih file</span>
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  Maksimal 5 file (Gambar/PDF).
                </p>
                <input
                  id="file-upload"
                  type="file"
                  multiple
                  accept="image/*,application/pdf"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <DialogFooter className="px-6 py-4 mb-2 border-t border-slate-100 bg-slate-50/80 backdrop-blur-sm shrink-0">
        <Button
          type="button"
          disabled={isSubmitting}
          className="w-full sm:w-auto"
          onClick={handleSubmit}
        >
          {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Kirim Pesan
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};
